/**
 * Callback Requests Drawer
 *
 * Displays pending callback requests for an account in a side drawer.
 * Allows account managers to acknowledge (complete) or cancel requests.
 */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { cn, formatDateTime } from '@/lib/utils';
import {
  getAccountCallbackRequests,
  completeCallbackRequest,
  cancelCallbackRequest,
  type CallbackRequest,
} from '../api';

interface CallbackRequestsDrawerProps {
  accountId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRequestsUpdated?: () => void;
}

export default function CallbackRequestsDrawer({
  accountId,
  open,
  onOpenChange,
  onRequestsUpdated,
}: CallbackRequestsDrawerProps) {
  const queryClient = useQueryClient();
  const [selectedRequest, setSelectedRequest] = useState<CallbackRequest | null>(null);
  const [notes, setNotes] = useState('');
  const [actionType, setActionType] = useState<'complete' | 'cancel' | null>(null);

  // Fetch pending callback requests for this account
  const { data, isLoading, error } = useQuery({
    queryKey: ['callback-requests', accountId, 'pending'],
    queryFn: () => getAccountCallbackRequests(accountId, { status: 'pending' }),
    enabled: open,
  });

  // Complete mutation
  const completeMutation = useMutation({
    mutationFn: ({ requestId, notes }: { requestId: string; notes?: string }) =>
      completeCallbackRequest(requestId, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['callback-requests', accountId] });
      queryClient.invalidateQueries({ queryKey: ['account-dashboard', accountId] });
      setSelectedRequest(null);
      setNotes('');
      setActionType(null);
      onRequestsUpdated?.();
    },
  });

  // Cancel mutation
  const cancelMutation = useMutation({
    mutationFn: ({ requestId, notes }: { requestId: string; notes?: string }) =>
      cancelCallbackRequest(requestId, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['callback-requests', accountId] });
      queryClient.invalidateQueries({ queryKey: ['account-dashboard', accountId] });
      setSelectedRequest(null);
      setNotes('');
      setActionType(null);
      onRequestsUpdated?.();
    },
  });

  const handleAction = (request: CallbackRequest, type: 'complete' | 'cancel') => {
    setSelectedRequest(request);
    setActionType(type);
    setNotes('');
  };

  const handleConfirmAction = () => {
    if (!selectedRequest || !actionType) return;

    if (actionType === 'complete') {
      completeMutation.mutate({ requestId: selectedRequest.id, notes: notes || undefined });
    } else {
      cancelMutation.mutate({ requestId: selectedRequest.id, notes: notes || undefined });
    }
  };

  const handleCancelAction = () => {
    setSelectedRequest(null);
    setNotes('');
    setActionType(null);
  };

  const formatDate = (dateString: string) => formatDateTime(dateString);

  const requests = data?.requests || [];
  const isActionPending = completeMutation.isPending || cancelMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PhoneIcon className="h-5 w-5 text-blue-600" />
            Pending Callback Requests
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-24 w-full" />
              ))}
            </div>
          ) : error ? (
            <div className="text-center py-8 text-red-600">
              Failed to load callback requests
            </div>
          ) : requests.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No pending callback requests
            </div>
          ) : (
            <div className="space-y-3">
              {requests.map((request) => (
                <div
                  key={request.id}
                  className={cn(
                    'border rounded-lg p-4 space-y-3',
                    selectedRequest?.id === request.id && 'ring-2 ring-blue-500'
                  )}
                >
                  {/* Account Info */}
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="font-medium">
                        {request.customerName || request.customerEmail}
                      </div>
                      <a
                        href={`mailto:${request.customerEmail}`}
                        className="text-sm text-blue-600 hover:underline"
                      >
                        {request.customerEmail}
                      </a>
                    </div>
                    <Badge variant="outline" className="text-xs">
                      {formatDate(request.requestedAt)}
                    </Badge>
                  </div>

                  {/* Project Info */}
                  {request.projectNumber && (
                    <div className="text-sm text-muted-foreground">
                      <span className="font-medium">Project:</span>{' '}
                      {request.projectNumber}
                      {request.projectName && ` - ${request.projectName}`}
                      {request.siteName && (
                        <>
                          <br />
                          <span className="font-medium">Site:</span> {request.siteName}
                        </>
                      )}
                    </div>
                  )}

                  {/* Action Buttons or Confirmation */}
                  {selectedRequest?.id === request.id ? (
                    <div className="space-y-3 pt-2 border-t">
                      <Textarea
                        placeholder={
                          actionType === 'complete'
                            ? 'Add notes about the call (optional)...'
                            : 'Reason for cancellation (optional)...'
                        }
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        rows={2}
                      />
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant={actionType === 'complete' ? 'default' : 'destructive'}
                          onClick={handleConfirmAction}
                          disabled={isActionPending}
                        >
                          {isActionPending
                            ? 'Processing...'
                            : actionType === 'complete'
                              ? 'Confirm Complete'
                              : 'Confirm Cancel'}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={handleCancelAction}
                          disabled={isActionPending}
                        >
                          Back
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex gap-2 pt-2">
                      <Button
                        size="sm"
                        variant="default"
                        className="bg-green-600 hover:bg-green-700"
                        onClick={() => handleAction(request, 'complete')}
                      >
                        <CheckIcon className="h-4 w-4 mr-1" />
                        Called Back
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleAction(request, 'cancel')}
                      >
                        Cancel Request
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {requests.length > 0 && (
          <div className="pt-4 border-t text-sm text-muted-foreground">
            {requests.length} pending request{requests.length !== 1 ? 's' : ''}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Icons
function PhoneIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
      />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  );
}
